import { UTCDate } from '@date-fns/utc';
import { Currency } from '@seapoint/server/enums';
import { format, subMonths } from 'date-fns';
import { TransactionView } from '../../../types/entity-types';

export const DATE_FORMAT = "MMM''yy";
const NUMBER_MONTHS_TO_SHOW = 4;
const today = new UTCDate();

// This is the format in which the chart library expects the data
// https://recharts.org/en-US/examples/StackedBarChart
export type MonthlyFinancialTotals = {
  month: string; // Format: "MMM'YY" i.e. Oct'24
  moneyIn: number;
  moneyOut: number;
};

export type VendorFinancials = {
  totals: {
    moneyIn: number;
    moneyOut: number;
  };
  monthlyTotals: MonthlyFinancialTotals[];
  currency: Currency;
};

export const getVendorFinancials = (transactions: TransactionView[]): VendorFinancials => {
  const monthChartLabels = getMonthChartLabels();
  const initialMonthlyData = initializeMonthlyData(monthChartLabels);

  const { totals, updatedMonthlyData } = calculateTransactionTotals(transactions, initialMonthlyData);

  // Adds the month chart label to each object in updatedMonthlyData
  const monthlyTotals = monthChartLabels.map((month) => ({
    month,
    ...updatedMonthlyData.get(month)!,
  }));

  return {
    totals,
    monthlyTotals,
    // All transactions have the base currency chosen by the user, so we can query any random transaction
    currency: transactions[0]?.baseCurrency,
  };
};

export const getUnassignedVendorFinancials = (): VendorFinancials => {
  const monthChartLabels = getMonthChartLabels();
  const initialMonthlyData = initializeMonthlyData(monthChartLabels);

  const monthlyTotals = monthChartLabels.map((month) => ({
    month,
    ...initialMonthlyData.get(month)!,
  }));

  return {
    totals: {
      moneyIn: 0,
      moneyOut: 0,
    },
    monthlyTotals,
    // No data will be displayed in the chart, so we can use any currency
    currency: Currency.EUR,
  };
};

export const getMonthChartLabels = (): string[] => {
  return Array.from({ length: NUMBER_MONTHS_TO_SHOW })
    .map((_, monthIndex) => {
      const previousMonth = subMonths(today, monthIndex);
      return format(previousMonth, DATE_FORMAT);
    })
    .reverse();
};

type MonthlyDataMap = Map<string, { moneyIn: number; moneyOut: number }>;

const initializeMonthlyData = (monthChartLabels: string[]): MonthlyDataMap => {
  const monthlyData = new Map<string, { moneyIn: number; moneyOut: number }>();

  monthChartLabels.forEach((month) => {
    monthlyData.set(month, { moneyIn: 0, moneyOut: 0 });
  });

  return monthlyData;
};

const calculateTransactionTotals = (
  transactions: TransactionView[],
  monthlyData: MonthlyDataMap,
): { totals: { moneyIn: number; moneyOut: number }; updatedMonthlyData: MonthlyDataMap } => {
  const totals = transactions.reduce(
    (acc, transaction) => {
      const transactionMonth = format(new UTCDate(transaction.fspDate), DATE_FORMAT);

      if (monthlyData.has(transactionMonth)) {
        const monthData = monthlyData.get(transactionMonth)!;

        if (transaction.isCredit) {
          // The aggregate data should be displayed in the base currency because the user chose to display reporting in that currency
          monthData.moneyIn += transaction.baseCurrencyAmount;
          acc.moneyIn += transaction.baseCurrencyAmount;
        } else {
          monthData.moneyOut += transaction.baseCurrencyAmount;
          acc.moneyOut += transaction.baseCurrencyAmount;
        }

        monthlyData.set(transactionMonth, monthData);
      }

      return acc;
    },
    { moneyIn: 0, moneyOut: 0 },
  );

  return { totals, updatedMonthlyData: monthlyData };
};
